<script setup lang="ts">
import { $t } from '@teachino/shared-i18n';
import { ref, onMounted, onUnmounted } from 'vue';
import TFileInput from '../Form/TFileInput.vue';
import TIcon from '../Icon/TIcon.vue';
import type { ButtonColor, ButtonType } from '../Button/button-types';

const props = withDefaults(
  defineProps<{
    multiple?: boolean;
    acceptedMimetypes?: string[];
    disablePointerEventsWhenNoDragActive?: boolean;
    /** Style of the default "choose files" button (defaults to filled/primary). */
    buttonType?: ButtonType;
    buttonColor?: ButtonColor;
  }>(),
  {
    multiple: false,
    acceptedMimetypes: () => [],
    buttonType: 'filled',
    buttonColor: 'primary',
  },
);

const emit = defineEmits<{
  filesSelected: [File[]];
}>();

// const dragOver = ref(false);
const globalDragActive = ref(false);
let dragCounter = 0;

// Global drag event handlers
function handleGlobalDragEnter(event: DragEvent) {
  dragCounter++;
  if (event.dataTransfer?.types.includes('Files')) {
    globalDragActive.value = true;
  }
}

function handleGlobalDragLeave() {
  dragCounter--;
  if (dragCounter === 0) {
    globalDragActive.value = false;
  }
}

function handleGlobalDrop() {
  dragCounter = 0;
  globalDragActive.value = false;
}

function handleGlobalDragOver(event: DragEvent) {
  // Only prevent default if files are being dragged
  if (event.dataTransfer?.types.includes('Files')) {
    event.preventDefault();
  }
}

// Setup and cleanup global event listeners
onMounted(() => {
  document.addEventListener('dragenter', handleGlobalDragEnter);
  document.addEventListener('dragleave', handleGlobalDragLeave);
  document.addEventListener('drop', handleGlobalDrop);
  document.addEventListener('dragover', handleGlobalDragOver);
});

onUnmounted(() => {
  document.removeEventListener('dragenter', handleGlobalDragEnter);
  document.removeEventListener('dragleave', handleGlobalDragLeave);
  document.removeEventListener('drop', handleGlobalDrop);
  document.removeEventListener('dragover', handleGlobalDragOver);
});

function handleDrop(event: DragEvent) {
  // Prevent default behavior (Prevent file from being opened)
  event.preventDefault();

  // Reset drag states
  // dragOver.value = false;
  dragCounter = 0;
  globalDragActive.value = false;

  const fileList = event.dataTransfer?.files;

  if (!fileList) {
    return;
  }

  if (!props.multiple && fileList.length > 1) {
    return;
  }

  const hasMimetypeFilter = props.acceptedMimetypes.length > 0;

  const fileArray = Array.from(fileList);

  const filteredFileList = hasMimetypeFilter
    ? fileArray.filter((file) => props.acceptedMimetypes.includes(file.type))
    : fileArray;

  if (filteredFileList.length === 0) {
    return;
  }

  emit('filesSelected', filteredFileList);
}
</script>

<template>
  <!-- These drag* events need to be handled for html to register something as a dropzone -->
  <!-- disable pointer events when currently nothing is dragged over, so that
e.g. emoji picker that might be open and overlapping with the dropzone can still be clicked  -->
  <div
    class="relative"
    :class="[
      disablePointerEventsWhenNoDragActive && !globalDragActive
        ? 'pointer-events-none'
        : '',
    ]"
    @drop.prevent="handleDrop"
  >
    <slot>
      <div>
        <div
          :class="[
            'flex min-h-36 flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-4 transition-all',
            globalDragActive
              ? 'border-primary bg-primary-50 text-primary'
              : 'text-neutral border-neutral-300 bg-white',
          ]"
        >
          <TIcon
            name="upload"
            :class="[
              'h-12 w-12 transition-all',
              globalDragActive ? 'jiggle' : '',
            ]"
          />
          <span>{{ $t('library.file_upload.drag_here') }}</span>
          <TFileInput
            :multiple="multiple"
            :accepted-mimetypes="acceptedMimetypes"
            :button-type="buttonType"
            :button-color="buttonColor"
            @files-selected="(files) => emit('filesSelected', files)"
          >
          </TFileInput>
        </div>
      </div>
    </slot>
    <div
      v-if="globalDragActive && $slots['drag-overlay']"
      class="absolute inset-0 rounded-md bg-neutral-200"
    >
      <slot name="drag-overlay"> </slot>
    </div>
  </div>
</template>
